import React, { useState } from "react";
import { Box } from "@mui/material";

import usePageBuilder from "./components/hooks/usePageBuilder";
import useCaseStudy from "./components/hooks/useCaseStudy";

import PageBuilderTopBar from "./components/layout/PageBuilderTopBar";
import SectionSidebar from "./components/layout/SectionSidebar";
import SectionCanvas from "./components/layout/SectionCanvas";
import PreviewPanel from "./components/layout/PreviewPanel";

import SectionModal from "./components/modals/SectionModal";
import OverviewModal from "./components/modals/OverviewModal";
import DetailModal from "./components/modals/DetailModal";
import CaseStudyModal from "./components/modals/CaseStudyModal";
import PublishConfirmModal from "./components/modals/PublishConfirmModal";

import { OVERVIEW_CONFIGS } from "./components/constants/overviewConfigs";

const OVERVIEW_SECTIONS = [
  "team",
  "features",
  "pricing",
  "testimonials",
  "faq",
  "caseStudies",
];

const PageBuilder = () => {
  const {
    apiUrl,
    navigate,
    routePageId,
    cloneFromId,
    effectiveId,
    form,
    setForm,
    saving,
    editingSection,
    setEditingSection,
    modalDraft,
    setModalDraft,
    openSectionModal,
    saveSectionModal,
    closeSectionModal,
    overviewSection,
    setOverviewSection,
    detailIndex,
    setDetailIndex,
    detailDraft,
    setDetailDraft,
    handleSave,
    saveToBackend,
  } = usePageBuilder();

  const {
    caseStudyModalOpen,
    caseStudyForm,
    setCaseStudyForm,
    savingCaseStudy,
    openCaseStudyModal,
    closeCaseStudyModal,
    handleAddCaseStudyToPage,
  } = useCaseStudy(apiUrl, effectiveId);

  const [detailSection, setDetailSection] = useState(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  // Preview panel toggle
  const [previewOpen, setPreviewOpen] = useState(false);

  // ── Handlers ──────────────────────────────

  const handleSectionClick = (key) => {
    setActiveSection(key);
    if (OVERVIEW_SECTIONS.includes(key)) {
      setOverviewSection(key);
    } else {
      openSectionModal(key);
    }
  };

  const getEmptyItem = (sectionKey) => {
    const config = OVERVIEW_CONFIGS[sectionKey];
    return config?.emptyItem?.() ?? {};
  };

  const handleAddNew = (sectionKey, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (sectionKey === "caseStudies") {
      openCaseStudyModal(form.caseStudies.items?.length || 0, null);
      return;
    }

    const emptyItem = getEmptyItem(sectionKey);
    setDetailSection(sectionKey);
    setDetailIndex(
      sectionKey === "pricing"
        ? form[sectionKey].packages?.length || 0
        : form[sectionKey].items?.length || 0,
    );
    setDetailDraft(emptyItem);
  };

  const handleOverviewCardClick = (index) => {
    setOverviewSection(null);
    setDetailSection(overviewSection);
    setDetailIndex(index);
    const config = OVERVIEW_CONFIGS[overviewSection];
    const items =
      overviewSection === "pricing"
        ? form[overviewSection].packages
        : form[overviewSection][config.itemsKey];
    setDetailDraft({ ...items[index] });
  };

  const handleOverviewAddClick = () => {
    if (overviewSection === "caseStudies") {
      openCaseStudyModal(form.caseStudies.items?.length || 0, null);
      setOverviewSection(null);
      return;
    }
    const config = OVERVIEW_CONFIGS[overviewSection];
    const items =
      overviewSection === "pricing"
        ? form[overviewSection].packages
        : form[overviewSection][config.itemsKey];
    setOverviewSection(null);
    setDetailSection(overviewSection);
    setDetailIndex(items.length);
    setDetailDraft(config.emptyItem());
  };

  const handleDetailClose = () => {
    setDetailIndex(null);
    setDetailDraft(null);
    setDetailSection(null);
  };

  // ─────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <PageBuilderTopBar
        routePageId={routePageId}
        cloneFromId={cloneFromId}
        form={form}
        effectiveId={effectiveId}
        saving={saving}
        onBack={() => navigate("/pages")}
        onSaveDraft={() => handleSave(false, false)}
        onPublish={() => setPublishModalOpen(true)}
        previewOpen={previewOpen}
        onTogglePreview={() => setPreviewOpen((v) => !v)}
      />

      <Box display="flex" flex={1} sx={{ overflow: "hidden" }}>
        <SectionSidebar
          form={form}
          onSectionClick={handleSectionClick}
          activeSection={activeSection}
        />

        {/* Canvas shrinks when preview is open */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            minWidth: 0, // prevents flex overflow
          }}
        >
          <SectionCanvas
            form={form}
            setForm={setForm}
            effectiveId={effectiveId}
            saveToBackend={saveToBackend}
            onSectionClick={handleSectionClick}
            onAddNew={handleAddNew}
            openCaseStudyModal={openCaseStudyModal}
            setOverviewSection={setOverviewSection}
            setDetailSection={setDetailSection}
            setDetailIndex={setDetailIndex}
            setDetailDraft={setDetailDraft}
          />
        </Box>

        {/* Live preview panel — mounts only when open */}
        {previewOpen && <PreviewPanel form={form} />}
      </Box>

      {/* ── Modals ── */}
      <SectionModal
        editingSection={editingSection}
        modalDraft={modalDraft}
        setModalDraft={setModalDraft}
        onClose={closeSectionModal}
        onSave={saveSectionModal}
        onOpenCaseStudy={(idx) => openCaseStudyModal(idx)}
      />

      <OverviewModal
        overviewSection={overviewSection}
        form={form}
        setForm={setForm}
        effectiveId={effectiveId}
        saveToBackend={saveToBackend}
        onClose={() => setOverviewSection(null)}
        onCardClick={handleOverviewCardClick}
        onAddClick={handleOverviewAddClick}
      />

      <DetailModal
        detailSection={detailSection}
        detailIndex={detailIndex}
        detailDraft={detailDraft}
        setDetailDraft={setDetailDraft}
        form={form}
        setForm={setForm}
        effectiveId={effectiveId}
        saveToBackend={saveToBackend}
        onClose={handleDetailClose}
      />

      <CaseStudyModal
        open={caseStudyModalOpen}
        caseStudyForm={caseStudyForm}
        setCaseStudyForm={setCaseStudyForm}
        savingCaseStudy={savingCaseStudy}
        onClose={closeCaseStudyModal}
        onConfirm={() => handleAddCaseStudyToPage(setForm, setModalDraft)}
      />

      <PublishConfirmModal
        open={publishModalOpen}
        slug={form.meta?.slug || ""}
        saving={saving}
        onClose={() => setPublishModalOpen(false)}
        onConfirm={() => handleSave(true)}
      />
    </Box>
  );
};

export default PageBuilder;
